import React, { useMemo } from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { BasicDoughnutChart, Icon, LogoLoader, LogoLoaderProps, Text } from "@performyard/py-components";
import { UserErrorResponseJson } from "@performyard/service-contracts";

import { InsightRenderer, formatInsightText } from "./RenderInsight";

import Button from "../../Button";
import IconButton from "../../Button/IconButton";
import { CopyToClipboard } from "../../CopyToClipboard";
import IconComponent from "../../Icon/Icon";
import Panel from "../../Panel";

interface AISidePanelProps<T extends { data: Record<string, unknown> }> {
	type: "review" | "engagement" | "review-cycle" | "survey" | "meeting";
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	data: T | null;
	isLoading: boolean;
	onRetry: () => void;
	renderHeader?: (data: T["data"], isChunking: boolean) => React.ReactNode;
	renderInfoSection?: (data: T["data"]) => React.ReactNode;
	error?: UserErrorResponseJson | null;
	logoLoaderProps?: LogoLoaderProps;
	sectionTitleMap?: Record<string, string>;
}

const INSIGHT_TITLES: Record<AISidePanelProps<any>["type"], string> = {
	review: "Review Insights",
	engagement: "Engagement Insights",
	meeting: "Meeting Workspace Insights",
	"review-cycle": "Review Cycle Insights",
	survey: "Survey Insights",
} as const;

export function AISidePanel<T extends { data: Record<string, unknown> }>({
	type,
	isOpen,
	onOpenChange,
	data,
	isLoading,
	onRetry,
	renderHeader,
	renderInfoSection,
	error,
	logoLoaderProps = {
		textArray: ["Loading...", "Please wait...", "Almost there..."],
		mainText: "Loading Insights...",
		switchTimer: 2000,
	},
	sectionTitleMap,
}: AISidePanelProps<T>) {
	const handleDismiss = () => onOpenChange(false);
	const unwrappedData = data?.data ?? null;

	const isChunking = useMemo(() => {
		return unwrappedData && "completedChunks" in unwrappedData && "totalChunks" in unwrappedData;
	}, [unwrappedData]);

	const chunkPercent = useMemo(() => {
		if (!isChunking || !unwrappedData) return null;
		const { completedChunks, totalChunks } = unwrappedData as any;
		return (completedChunks / totalChunks) * 100;
	}, [unwrappedData, isChunking]);

	const chunkProgress = useMemo(() => {
		if (!isChunking || !unwrappedData) return null;
		const { completedChunks, totalReviews } = unwrappedData as {
			completedChunks: number;
			totalReviews?: number;
		};

		let title = "";
		switch (type) {
			case "review":
			case "review-cycle":
				title = "Reviews";
				break;
			case "engagement":
				title = "Questions";
				break;
			case "survey":
				title = "Responses";
				break;
		}

		// For review-cycle with totalReviews, show more detailed progress
		// - While processing individual reviews (level 0): show "X of Y Reviews Analyzed"
		// - While generating final insights (level 1+): show "Generating Final Insights..."
		if (type === "review-cycle" && totalReviews !== undefined) {
			const reviewsCompleted = Math.min(completedChunks, totalReviews);
			if (completedChunks < totalReviews) {
				return `Analyzing ${title} - ${reviewsCompleted} of ${totalReviews}`;
			}
			if (completedChunks === totalReviews) {
				return `Generating Final Insights...`;
			}
			// completedChunks > totalReviews means we're in the merge phase
			return `Finalizing Insights...`;
		}

		return `Processing ${title} - ${completedChunks} Completed`;
	}, [unwrappedData, isChunking, type]);

	const insight = useMemo(() => {
		if (!unwrappedData) return null;
		return unwrappedData.insight;
	}, [unwrappedData]);

	return (
		<Panel side="right" isOpen={isOpen} onClose={handleDismiss} width={416}>
			{error ? (
				<div className="flex h-full min-h-[300px] flex-col items-center justify-center whitespace-pre-wrap px-4 text-center">
					<div className="mb-2 aspect-square w-fit rounded-full bg-red-1 p-1">
						<Icon icon={faExclamationCircle} color="red-5" size={24} />
					</div>
					<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
						{error.message || "An unexpected error occurred."}
					</Text>
				</div>
			) : data || isLoading || isChunking ? (
				<>
					{isLoading && !isChunking ? (
						/* 2. Generic loading state (no chunking) */
						<div className="flex h-full flex-col items-center justify-center px-4 text-center">
							<LogoLoader {...logoLoaderProps} />
						</div>
					) : (
						<div className="flex h-full flex-col gap-4 px-4">
							{/* Header */}
							<div className="sticky top-0 z-10 flex flex-col items-center justify-between gap-4 bg-white pt-4">
								<div className="flex w-full flex-row items-center justify-between">
									<div className="flex items-center gap-2 font-regular">
										<IconComponent icon={faSparkles} color="neutral-9" size="regular" />
										<Text size="paragraph-2" weight="regular" color="neutral-9">
											PerformYard
										</Text>
									</div>
									<IconButton
										testId="leave-feedback-close-button"
										icon={faXmark}
										onClick={handleDismiss}
									/>
								</div>
								<div className="flex w-full flex-row items-center justify-between">
									{/* Render Additional Header Information */}
									{renderHeader && unwrappedData && renderHeader(unwrappedData, isChunking ?? false)}
								</div>
							</div>

							{/* Insights */}
							{isChunking ? (
								<div className="flex h-full flex-col px-2">
									{/* Render info stays pinned at the top */}
									{renderInfoSection && unwrappedData && (
										<div className="flex flex-col gap-1 font-sans text-xs font-normal leading-4 text-neutral-900">
											{renderInfoSection(unwrappedData)}
										</div>
									)}

									{/* Fills the rest of the height and centers content */}
									<div className="flex flex-1 items-center justify-center px-4 text-center">
										<div
											className="flex flex-col items-center gap-3"
											role="status"
											aria-live="polite"
										>
											<BasicDoughnutChart percent={chunkPercent || 0} size={168} />
											<p className="text-sm text-neutral-600">{chunkProgress}</p>
										</div>
									</div>
								</div>
							) : (
								<>
									<div className="flex flex-1 flex-col gap-4 overflow-y-auto px-2">
										{/* Summarization Cache Info if available */}

										{renderInfoSection && unwrappedData && (
											<div className="flex flex-col gap-1 font-sans text-xs font-normal leading-4 text-neutral-900">
												{renderInfoSection(unwrappedData)}
											</div>
										)}
										<div className="flex items-center gap-2">
											<IconComponent icon={faSparkles} color="primary-5" size="small" />
											<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
												{INSIGHT_TITLES[type]}
											</Text>
										</div>
										{insight ? (
											<>
												<InsightRenderer
													insight={insight as Record<string, unknown>}
													titleMap={sectionTitleMap}
												/>
											</>
										) : (
											<Text size="paragraph-3" weight="regular" color="neutral-9">
												No insights available.
											</Text>
										)}
									</div>

									{/* Footer */}
									<div className="sticky bottom-0 flex items-center justify-between gap-2 border-t bg-white py-4">
										<Button
											testid="re-summarize-button"
											icon={faSparkles}
											color="blue"
											size="medium"
											onClick={onRetry}
											className="flex-1"
											disabled={isLoading}
										>
											Re-summarize
										</Button>
										<Button isHollow size="medium" className="flex-1" disabled={isLoading}>
											<CopyToClipboard
												text={
													insight ? formatInsightText(insight as Record<string, unknown>) : ""
												}
											>
												<p>Copy to Clipboard</p>
											</CopyToClipboard>
										</Button>
									</div>
								</>
							)}
						</div>
					)}
				</>
			) : null}
		</Panel>
	);
}
